import React, { useState } from 'react';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { Bar } from 'react-chartjs-2';
import PropTypes from 'prop-types';
import CommonContainer from '../../../common/CommonContainer';
import SalesOverviewReportForm from './SalesOverviewReportForm';
import RegularDataGrid from '../../../common/table/RegularDataGrid';
import DataGridCell from '../../../common/table/DataGridCell';
import { isNumber } from '../../../../utils/report-utils';
import { Tooltip, useTheme } from '@material-ui/core';
import { getLabels, getValues, prepareBarChartData } from '../../../../utils/chart-utils';
import { EHK_SALES_OVERVIEW } from '../standard/StandardReportConfig';
import { SALES_OVERVIEW } from '../../../common/charts/chartDataConfig';
import { ThemeContext } from '../../../../config/theme';
import { ALMOST_WHITE_COLOR, BLACK_COLOR } from '../../../../const/color-const';
import { TITLE_SIZE } from '../../../../const/chart-const';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {
  clearStandardReports,
} from '../../../../actions/report-actions';
import CommonSnackbar from '../../../common/CommonSnackbar';
import { InfoOutlined } from '@material-ui/icons';
import IconButton from '@material-ui/core/IconButton';
import MoneyFormat from '../../../common/MoneyFormat';
import NoDataAvailable from '../../../common/charts/NoDataAvailable';
import { DownloadToolbar } from '../sie4/Sie4ReportConfig';
import { downloadTransactionFileForExcel } from '../../../../actions/sie4-actions';

const useStyles = makeStyles(theme => ({
  salesHeader: {
    alignSelf: 'center',
    paddingTop: theme.spacing(3),
  },
  tableWrapper: {
    display: 'flex',
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(8),
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      margin: '12px auto',
    },
  },
  noDataFound: {
    marginTop: theme.spacing(4),
    fontSize: 16,
  },
  buttonsWrapper: {
    marginTop: theme.spacing(2),
    '& .CommonButton-button-587': {
      maxHeight: 34,
    },
  },
  chartWrapper: {
    margin: theme.spacing(8, 0, 18, 0),
    paddingTop: theme.spacing(4),
    width: '100%',
    maxWidth: '100%',
    [theme.breakpoints.up('md')]: {
      maxWidth: '80%',
    },
  },
  tooltipContainer: {
    position: 'relative',
    width: '100%',
    '& button': {
      position: 'absolute',
      right: '15%',
      top: 0,
      bottom: 0,
    },
  },
}));

export const chartOptions = (data, theme, darkState) => ({
  scales: {
    yAxes: [
      {
        gridLines: {
          display: false,
        },
        scaleLabel: {
          display: !!data.money,
          fontColor: darkState ? ALMOST_WHITE_COLOR : BLACK_COLOR,
          labelString: data.money && 'kr',
        },
        ticks: {
          padding: theme.spacing(2),
          fontColor: darkState ? ALMOST_WHITE_COLOR : BLACK_COLOR,
        },
      },
    ],
    xAxes: [
      {
        gridLines: {
          display: false,
        },
        ticks: {
          fontColor: darkState ? ALMOST_WHITE_COLOR : BLACK_COLOR,
        },
      },
    ],
  },
  title: {
    display: true,
    fontSize: TITLE_SIZE,
    text: data.title,
  },
  legend: {
    display: false,
  },
  cornerRadius: 6,
  maintainAspectRatio: false,
});

const DownloadToolbarWithProgress = ({
                                       reportName,
                                       formValues,
                                       machinesOptions,
                                       downloadTransactionFileForExcel,
                                     }) => {
  const [downloadProgress, setDownloadProgress] = useState(null);
  
  return (
    <DownloadToolbar
      reportName={reportName}
      formValues={formValues}
      machinesOptions={machinesOptions}
      setDownloadProgress={setDownloadProgress}
      downloadProgress={downloadProgress}
      downloadTransactionFileForExcel={downloadTransactionFileForExcel}
    />
  );
};

const SalesOverviewReport = ({ machines, reports, clearStandardReports, downloadTransactionFileForExcel }) => {
  const machinesOptions = machines.map(machine => ({ label: machine.Name, value: machine.Id }));
  const reportsLength = reports && (reports.length || 0);
  const [formValues, setFormValues] = useState(null);
  const { reportName, fields } = EHK_SALES_OVERVIEW;
  const { formatMessage } = useIntl();
  
  const SALES_OVERVIEW_HEADERS = [
    { apiHeader: 'Period', reportName: 'Period' },
    {
      apiHeader: '**TOTAL**|ANTAL KVITTON',
      reportName: formatMessage({ id: 'sales.overview.column.antal', defaultMessage: 'Amount of transactions' }),
    },
    {
      apiHeader: 'PRESENTKORT|BELOPP',
      reportName: formatMessage({ id: 'sales.overview.column.giftcards', defaultMessage: 'Sold gift cards' }),
    },
    {
      apiHeader: '**TOTAL**|BELOPP',
      reportName: formatMessage({ id: 'sales.overview.column.total', defaultMessage: 'Total' }),
    },
  ];
  
  const sortByPeriod = (a, b) => {
    const periodA = a?.Period || '';
    const periodB = b?.Period || '';
    return periodA.localeCompare(periodB);
  };
  
  const reportsData = reports && reports
    .filter(({ Period }) => Period !== '**TOTAL**')
    .sort(sortByPeriod);
  const soldGiftCardsHeader = SALES_OVERVIEW_HEADERS[2].apiHeader;
  const classes = useStyles();
  const theme = useTheme();
  
  const renderTotalCell = (params) => {
    return <div className={classes.tooltipContainer}>
      <MoneyFormat value={params.value} />
      {areThereSoldGiftCardsInCurrenRow(params.row) && <Tooltip title={formatMessage({
        id: 'sold.gift.cards.notification',
        defaultMessage: 'Sold gift cards are included in Total column',
      })}>
        <IconButton>
          <InfoOutlined fontSize={'small'} />
        </IconButton>
      </Tooltip>}
    </div>;
  };
  
  React.useEffect(() => {
    clearStandardReports();
  }, [clearStandardReports]);
  
  const data = {
    ...SALES_OVERVIEW,
    values: reports && getValues(SALES_OVERVIEW.valueFields, reportsData),
    labels: reports && getLabels(SALES_OVERVIEW.labelFields, reportsData),
    title: formatMessage({ id: `report.chart.SalesOverview`, defaultMessage: 'Sales Overview' }),
  };
  
  const getSalesOverviewHeaders = (reports) => {
    const reportHeaders = Object.keys(reports);
  
    return SALES_OVERVIEW_HEADERS.filter(item => reportHeaders.includes(item.apiHeader)).map(item => ({
      headerName: item.reportName,
      field: item.apiHeader,
      headerAlign: 'center',
      outline: 'none',
      align: 'center',
      width: 240,
      valueGetter: (params) => isNumber(params.value) ? Number(params.value) : params.value,
      renderCell: (params) => params.field === '**TOTAL**|BELOPP' ? renderTotalCell(params) : <DataGridCell
        value={params.value} />,
    }));
  };
  
  const getSalesOverviewRows = (reports) => {
    const translation = formatMessage({ id: 'sales.overview.column.total', defaultMessage: 'Total' });
    return reports.map((report, index) => ({
      ...report,
      id: index,
      Period: report.Period === '**TOTAL**' ? translation : report.Period,
    }));
  };
  
  const areThereSoldGiftCardsInReport = (reports) => {
    if (reportsLength > 1) {
      return Object.keys(reports[0])?.includes(soldGiftCardsHeader) ?? false;
    }
    return false;
  };
  
  const areThereSoldGiftCardsInCurrenRow = (row) => {
    return row[soldGiftCardsHeader] ?? false;
  };
  
  return (
    <CommonContainer hAlign="center">
      <h1 className={classes.salesHeader}>
        <FormattedMessage
          id="menu.main.reports.sales-overview"
          defaultMessage="Sales overview"
        />
      </h1>
      
      <SalesOverviewReportForm
        machines={machines}
        reportName={reportName}
        fields={fields}
        setFormValues={setFormValues}
      />
      
      {reportsData?.length > 2 && <div className={classes.chartWrapper}>
        <ThemeContext.Consumer>
          {({ darkState }) =>
            <Bar data={prepareBarChartData(data)}
                 options={chartOptions(data, theme, darkState)}
                 height={400}
            />}
        </ThemeContext.Consumer>
      </div>}
  
      {reportsLength > 1 && areThereSoldGiftCardsInReport(reports) && <CommonSnackbar
        title={<FormattedMessage id="notification" defaultMessage="Remember" />}
        message={<FormattedMessage id="sold.gift.cards.notification"
                                   defaultMessage="Sold gift cards are included in the Total column" />}
        open={true}
      />}
  
      {reportsLength > 1 && <RegularDataGrid
        reportName={'SalesOverview'}
        columns={getSalesOverviewHeaders(reports[0])}
        rows={getSalesOverviewRows(reports.sort(sortByPeriod))}
        components={{
          Toolbar: () => (
            <DownloadToolbarWithProgress
              reportName={reportName}
              formValues={formValues}
              machinesOptions={machinesOptions}
              downloadTransactionFileForExcel={downloadTransactionFileForExcel}
            />
          ),
        }}
      />}
  
      {reportsLength === 0 && <NoDataAvailable />}

    </CommonContainer>);
};

SalesOverviewReport.propTypes = {
  machines: PropTypes.array,
  reports: PropTypes.array,
  clearStandardReports: PropTypes.func,
  downloadTransactionFileForExcel: PropTypes.func,
};

const decorate = compose(
  connect(
    store => ({
      machines: store.user.machines,
      reports: store.report.standard,
    }),
    dispatch => bindActionCreators({
      clearStandardReports,
      downloadTransactionFileForExcel,
    }, dispatch)),
);

export default decorate(SalesOverviewReport);
